import { RoseTree } from '@tableau/data-structures';
import { DestinationItem, DestinationItemType, NestedItemBasicInfo, NestedItemsFractalRowStore, NestedItemsTreeHelper, NestedProjectExplorer } from '@tableau/nested-project-explorer';
import React from 'react';
import _ from 'underscore';

import { createMultipleItemResultNotificationValues, IErrorDetail, MultipleItemResultNotificationValues } from '~commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { openReactAlertDialog } from '~commonInfra/dialogs/ReactAlertDialog';
import { openReactConfirmationDialog, ReactConfirmationDialogBodyProps } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~commonInfra/L10n/L10n';
import { HelpService } from '~commonInfra/services/HelpService';
import { CommonErrorCode, ContentErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Location } from '~commonInfra/utils/Location';
import { Set } from '~commonInfra/utils/Set';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { IDatabaseInfo } from '~src/catalog/models/IDatabaseInfo';
import { Formatters, Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { IDataRoleBase } from '~src/dataRole/models/IDataRoleInfo';
import { DataRoles } from '~src/dataRole/resources/Dataroles';
import { DataRoleErrors } from '~src/dataRole/services/DataRoleErrors';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { DataSourceErrors } from '~src/datasource/services/DataSourceErrors';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { hasDraftIdPrefix } from '~src/drafts/utils/DraftUtils';
import { FlowBaseInfo } from '~src/flow/models/FlowInfo';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IMultipleContentTypeResult,IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { ILensBase } from '~src/lens/models/ILensInfo';
import { LensErrors } from '~src/lens/services/LensErrors';
import { IMetricInfo } from '~src/metrics/models/IMetricInfo';
import { MetricContentType } from '~src/metrics/models/MetricContentType';
import { MetricErrors } from '~src/metrics/services/MetricErrors';
import { selectPersonalSpace } from '~src/personalSpace/selectors/PersonalSpaceSelector';
import { IPredictiveModelBase, IPredictiveModelInfo } from '~src/predictiveModel/models/IPredictiveModelInfo';
import { PredictiveModelErrors } from '~src/predictiveModel/services/PredictiveModelErrors';
import { IResultWithDestinationProject } from '~src/projects/actions/MoveToProjectAction';
import { ProjectErrors } from '~src/projects/actions/ProjectErrors';
import { IProjectBase } from '~src/projects/models/IProjectInfo';
import { DestinationProjectMapper } from '~src/projects/models/mappers/DestinationProjectMapper';
import { DestinationProjectContentType, DestinationProjects } from '~src/projects/resources/DestinationProjects';
import { Projects } from '~src/projects/resources/Projects';
import { ContentItem } from '~src/projects/ServerApi/requests/MoveContentToContainerRequest';
import { IDestinationProject } from '~src/projects/ServerApi/types/IDestinationProject';
import { IProjectName } from '~src/projects/ServerApi/types/IProject';
import { IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnectionErrors } from '~src/publishedConnection/services/PublishedConnectionErrors';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { MoveContentDialogBody, MoveContentDialogBodyProps, MoveContentDialogBodyState } from '~src/spacesPlatform/components/content/MoveContentDialogBody';
import { getExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import {
  DatabaseContentType,
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  FlowDraftContentType,
  IContentType,
  LensContentType,
  PredictiveModelContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  TableContentType,
  WorkbookContentType} from '~src/spacesPlatform/models/ContentTypes';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IIdItemInfo, INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

export namespace MoveContentAction {

  const NoPermission = 'NO_PERMISSION';

  export function confirmMoveContent(content: IContentInfo[]): Promise<IResultWithDestinationProject> {

    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectContentType.name));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookContentType.name));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceContentType.name));
    const databases = Set.fromIdItemArray(content.filter(i => i.contentType === DatabaseContentType.name));
    const tables = Set.fromIdItemArray(content.filter(i => i.contentType === TableContentType.name));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowContentType.name));
    const dataRoles = Set.fromIdItemArray(content.filter(i => i.contentType === DataRoleContentType.name));
    const lenses = Set.fromIdItemArray(content.filter(i => i.contentType === LensContentType.name));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === MetricContentType.name));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === PublishedConnectionContentType.name));
    const predictiveModels = Set.fromIdItemArray(content.filter(i => i.contentType === PredictiveModelContentType.name));
    const count = content.length;
    let selectedItem: NestedItemBasicInfo = null;
    let moveDependentContent: boolean = true;
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const personalSpaceStatus = _.some(content, (item) => item.owner && item.owner.id === userId) && getSessionManagementServiceInstance().isPersonalSpaceOnSiteEnabled();

    return initNestedProjectExplorer(projects, workbooks, datasources, databases, flows, dataRoles, lenses, metrics, publishedConnections, predictiveModels, personalSpaceStatus, tables).then((result) => {
      if (result === NoPermission) {
        return alertCantMoveContent(Messages.permissionsLongMessage_cannotMoveItems({ ITEM_COUNT_LOC: Formatters.formatCount(content.length) })).result;
      }

      const actionDisabled = (bodyProps: MoveContentDialogBodyProps, dialogState: MoveContentDialogBodyState) => {
        return dialogState.selectedItem === null;
      };

      const movingDatabases = getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled() && databases.size() > 0;
      const movingTables = getSessionManagementServiceInstance().tablesInProjectsUIEnabled();
      const dialogInstance = openReactActionDialog<MoveContentDialogBodyProps, MoveContentDialogBodyState, any>({
        title: movingDatabases||movingTables ? Messages.moveAssets() : Messages.moveContent(),
        initialBodyProps: {
          prompt: Messages.selectProjectForContent({ ITEM_COUNT: count, ITEM_NAME: L10n.elideIfLong(content[0].name), ITEM_COUNT_LOC: Formatters.formatCount(count) }),
          targets: result as NestedProjectExplorer.Props,
          movingDatabases: movingDatabases,
          tablesInProjectsUIFeatureEnabled : movingTables,
        },
        initialDialogState: {
          selectedItem: selectedItem,
          moveDependentContent: moveDependentContent
        },
        bodyComponent: MoveContentDialogBody,
        actionName: movingDatabases||movingTables ? Messages.confirmMoveAssets({ ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count) }) : Messages.confirmMoveContent({ ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count) }),
        action: (props: MoveContentDialogBodyProps, state: MoveContentDialogBodyState) => {
          selectedItem = state.selectedItem;
          if (selectedItem.itemType === DestinationItemType.personalSpace) {
            return null;
          }
          let moveDependentContent = movingTables ? state.moveDependentContent : true;
          return moveContent(projects, workbooks, datasources, databases, tables, flows, dataRoles, lenses, metrics, publishedConnections, content, state.selectedItem, moveDependentContent);
        },
        actionDisabled,
        isActionDelete: false,
        dismissDialogOnActionFailed: true,
        dialogWidth: TabDialogWidth.width375px
      }) as any;

      return dialogInstance.result.then((result: any) => {
        dialogInstance.dismiss();

        if (selectedItem.itemType === DestinationItemType.personalSpace) {
          const contentItems = getContentItemsList(content, false);
          const contentName = getContentName(workbooks, datasources, predictiveModels, contentItems.length);
          const modalInstance = openReactConfirmationDialog({
            title: Messages.moveToPersonalSpaceWarningTitle(),
            message: Messages.moveToPersonalSpaceWarningPrimaryMessage({ITEM_COUNT: contentItems.length, CONTENT_TITLE: contentName}),
            actionName: Messages.moveToPersonalSpaceWarningAction(),
            bodyComponent: getPersonalSpaceWarningDialog,
            action: () => {
              return moveContent(projects, workbooks, datasources, databases, tables, flows, dataRoles, lenses, metrics, publishedConnections, content, selectedItem, false);
            },
            dialogWidth: TabDialogWidth.width500px,
            paramsForAction: null,
          });
          return Promise.resolve(modalInstance.result);
        }
        return { ...ResultHelper.combineResults(result), destinationProjectId: getDestinationProjectIdFromDialog(selectedItem) };
      });
    });
  }

  export function getPersonalSpaceWarningDialog(props: ReactConfirmationDialogBodyProps): JSX.Element {
    return(<div>
              <div>
                {props.message}
              </div>
              <div style ={{marginTop: '20px'}}>
                {Messages.moveToPersonalSpaceWarningSecondaryMessage() + ' '}
                <a href={HelpService.getHelpUrlByCategory('server-personal-move')} target="_blank" rel="noopener noreferrer">{Messages.moveToPersonalSpaceWarningLearnMore()}</a>
              </div>
          </div>);
  }

  function getContentName(workbooks: Set<IContentInfo>, datasources: Set<IContentInfo>, predictiveModels: Set<IContentInfo>, numberOfItems: number) {
    if (numberOfItems === 1 && workbooks.size() === 1) {
      return workbooks.getByIndex(0).name;
    } else if (numberOfItems === 1 && datasources.size() === 1) {
      return datasources.getByIndex(0).name;
    } else if (numberOfItems === 1 && predictiveModels.size() === 1) {
      return predictiveModels.getByIndex(0).name;
    }

    return '';
  }

  function alertCantMoveContent(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotMoveItems(),
      message
    });
  }

  function getDestinationProjectIdFromDialog(selectedProject: NestedItemBasicInfo): string {
    const destinationProjectId = selectedProject.id;
    return (destinationProjectId === NestedItemsFractalRowStore.SentinelRootProjectId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  function initNestedProjectExplorer(
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    lenses: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    predictiveModels: Set<IContentInfo>,
    personalSpaceStatus: boolean,
    tables: Set<IContentInfo>
  ): Promise<{}>{

    // The Nested Project Explorer presents the user a tree-view showing all the projects on the site,
    // along with their "status" (whether or not the items can actually be moved there).
    // We call the getDestinationProjects API to fetch the information to build this tree, however,
    // due to the limitations of the API, we split the source items by content type as well
    // as in groups of 1000 within each content type, and call the API separately for each of these chunks.
    //
    // The backend returns a complete list of Destination Projects for each chunk of source items
    // (let's call each copy a "slide"). Once we have all the slides, we combine them to create a
    // "projection" of all their statuses to show the user (the user must be able to move all the
    // items to a particular destination in order for it to be valid).
    let slidePromises = [];

    function pluckIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId);
    }

    // flow drafts are not permissioned as a full content type, filter out flow draft ids, the backend will then
    // fall back to user permissions for those ones
    function pluckFlowIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId).filter((id) => {
        return !hasDraftIdPrefix(id);
      });
    }

    function pluckVizportalIds(items: Set<IContentInfo>) {
      return items.map(item => item.vizportalId);
    }

    if (projects.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'project', pluckIds(projects)));
    }
    if (workbooks.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'workbook', pluckIds(workbooks)));
    }
    if (datasources.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'datasource', pluckIds(datasources)));
    }
    if (databases.size() > 0 && FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'database', pluckVizportalIds(databases)));
    }
    if (tables.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'table', pluckVizportalIds(tables)));
    }
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled() && flows.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'flow', pluckFlowIds(flows)));
    }
    if (dataRoles.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'dataRole', pluckIds(dataRoles)));
    }
    if (lenses.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'lens', pluckIds(lenses)));
    }
    if (metrics.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'metric', pluckIds(metrics)));
    }
    if (publishedConnections.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'virtualConnection', pluckIds(publishedConnections)));
    }
    if (predictiveModels.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'predictiveModel', pluckIds(predictiveModels)));
    }

    return Promise.all(slidePromises).then((slides: RoseTree<NestedItemBasicInfo>[][]) => {

      // If all the slides are null, it means the user has no permission to move any of the content
      if (slides.every(x => x === null)) {
        return NoPermission;
      }
      let showPersonalSpaceOption =  isPersonalSpaceAvailable();
      personalSpaceStatus = personalSpaceStatus && isPersonalSpaceSupported(workbooks, predictiveModels);
      return {
        topLevelDestinationStatus: getTopLevelDestinationStatus(datasources, databases, flows, workbooks, dataRoles, lenses, metrics, publishedConnections, predictiveModels),
        projects: makeDestinationItemSlideProjection(slides, showPersonalSpaceOption, personalSpaceStatus)
      };
    });
  }

  export function isPersonalSpaceSupported(...contentArgs: Set<IContentInfo>[]): boolean {
    return contentArgs.some((content) => isPersonalSpaceSupportedForContent(content));
  }

  function isPersonalSpaceSupportedForContent(content: Set<IContentInfo>): boolean {
    if (content.isEmpty()) {
      return false;
    }
    const contentType = content.first().contentType;
    const supportedContentTypes = [ WorkbookContentType.name, PredictiveModelContentType.name ];

    return supportedContentTypes.includes(contentType);
  }

  export function isPersonalSpaceAvailable(): boolean {
    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    if (!getSessionManagementServiceInstance().isPersonalSpaceOnSiteEnabled() || personalSpaceInfo.readOnly) {
      return false;
    }

    return true;
  }

  function addPersonalSpaceToProjection(projection: RoseTree<NestedItemBasicInfo>[], personalSpaceStatus: boolean) {
    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    let personalSpace: DestinationItem = {
      id: personalSpaceInfo.luid,
      name: '', // This is not used for PS but is a required field so have to be set
      itemType: DestinationItemType.personalSpace,
      childItemCount: 0,
      topLevelItem: true,
      status: personalSpaceStatus ? 'valid' : 'read_only',
    };
    return projection.concat(NestedItemsTreeHelper.convertItemListToTrees([personalSpace]));
  }

  //export for unit testing
  export function makeDestinationItemSlideProjection(slides: RoseTree<NestedItemBasicInfo>[][], showPersonalSpace: boolean, personalSpaceStatus: boolean) {
    let projection: RoseTree<NestedItemBasicInfo>[] = null;
    slides.forEach((slide) => {
      if (slide === null) {
        return;
      } else if (projection === null) {
        projection = slide;
      } else {
        projection = addSlideToProjection(slide, projection);
      }
    });
    if (showPersonalSpace) {
      projection = addPersonalSpaceToProjection(projection, personalSpaceStatus);
    }

    return projection;
  }

  function addSlideToProjection(slide: RoseTree<NestedItemBasicInfo>[],
    projection: RoseTree<NestedItemBasicInfo>[]) {
    slide.forEach((node, i) => {
      if (node.node.hasChildren) {
        addSlideToProjection(node.children, projection[i].children);
      }

      // TODO: Should we have a precedence of the statuses here? Perhaps have a new status
      // that combines the messaging for the various statuses if they're different?
      if (node.node.status !== 'valid') {
        projection[i].node.status = node.node.status;
      }
    });
    return projection;
  }

  // export for unit testing
  export function getTopLevelDestinationStatus(
    datasources: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    lenses: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    predictiveModels: Set<IContentInfo>,
  ) {
    if (workbooks.size() > 0 || datasources.size() > 0 || databases.size() > 0 || flows.size() > 0 || dataRoles.size() > 0 || lenses.size() > 0 ||
        metrics.size() > 0 || publishedConnections.size() > 0 || predictiveModels.size() >  0) {
      return 'structurally_invalid';
    } else if (!getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  function getDestinationProjectTreeSlidePromisesForSourceItems(
    query: string,
    contentType: DestinationProjectContentType,
    itemIds: string[],
  ): Promise<RoseTree<NestedItemBasicInfo>[]>[] {

    // We can send only 1000 IDs to the API at a time, so make chunks of 1000 IDs and do the queries in parallel.
    const idBlocks = RequestHelper.makeBlocks(itemIds, RequestHelper.DefaultBlockSize);
    return idBlocks.map(idBlock => {
      const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(
        contentType, idBlock, query);

      return SliceableHelper.fetchAll(projectsQuery).then(createDestinationProjectTreeSlide);
    });
  }

  function createDestinationProjectTreeSlide(projects: ListResult<IDestinationProject>): RoseTree<NestedItemBasicInfo>[] {
    // Use the result of getDestinationProjects to infer if the user can move the items.
    // If no items are returned, the user doesn't have permission to move some of the selected items.
    // The check needs to be items[0] being undefined as the response that comes back is actually an array with first item as undefined.
    if (projects.items[0] === undefined) {
      return null;
    }

    const destinationProjects = DestinationProjectMapper.map(projects.items);
    return NestedItemsTreeHelper.convertItemListToTrees(destinationProjects);
  }

  //export for unit testing
  export function moveContent(
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    tables: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    lenses: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    content: IContentInfo[],
    selectedItem: NestedItemBasicInfo,
    moveDependentContent: boolean,
  ) {
    const destinationProjectId = getDestinationProjectIdFromDialog(selectedItem);
    let moveFunctions: any[] = [];
    if(selectedItem.itemType === DestinationItemType.personalSpace) {
        moveFunctions.push(moveContentToContainer(
        selectedItem.id,
        selectedItem.itemType,
        getContentItemsList(content, moveDependentContent),
        content,
        selectedItem.name.length === 0 ? undefined: selectedItem.name));
    } else {
      moveFunctions = [
        moveDataRoles(dataRoles, selectedItem, destinationProjectId),
      ];
      let allContent: IContentInfo[] = getContentForMove(content);
      if (allContent.length > 0) {
        moveFunctions.push(moveContentToContainer(
          selectedItem.id,
          selectedItem.itemType,
          getContentItemsList(allContent, moveDependentContent),
          allContent,
          selectedItem.name.length === 0 ? undefined: selectedItem.name));
      }
    }
    return Promise.all(moveFunctions);
  }

  // This method is to collect all the content items to be used in with the new moveContentToContainer API
  // This method will go away once all the content types start using the new API
  function getContentForMove(content: IContentInfo[]) {

    let workbookAndDataSources: IContentInfo[] = content.filter(c => {
      return (c.contentType === WorkbookContentType.name || c.contentType === DatasourceContentType.name);
    });

    let databases: IContentInfo[] = content.filter(c=> {
      return (c.contentType === DatabaseContentType.name);
    });

    let tables: IContentInfo[] = content.filter(c=> {
      return (c.contentType === TableContentType.name);
    });

    let flows: IContentInfo[] = [];
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      flows = content.filter(c => {
        return c.contentType === FlowContentType.name && c.draftStatus !== ContentDraftStatus.unpublished;
      });
    }

    let flowDrafts: IContentInfo[] = [];
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      flowDrafts = content.filter(c => {
        return c.contentType === FlowContentType.name && c.draftStatus === ContentDraftStatus.unpublished;
      });
    }

    let metrics: IContentInfo[] = content.filter(c => {
      return (c.contentType === MetricContentType.name);
    });

    let virtualConnections: IContentInfo[] = content.filter(c => {
      return (c.contentType === PublishedConnectionContentType.name);
    });

    let projects: IContentInfo[] = content.filter(c => {
      return (c.contentType === ProjectContentType.name);
    });

    let predictiveModels: IContentInfo[] = content.filter(c => {
      return (c.contentType === PredictiveModelContentType.name);
    });

    let lenses: IContentInfo[] = content.filter(c => {
      return (c.contentType === LensContentType.name);
    });

    return [...workbookAndDataSources, ...databases, ...tables, ...flows, ...flowDrafts, ...metrics, ...projects, ...predictiveModels, ...lenses, ...virtualConnections];
  }

  function getContentItemsList(content: IContentInfo[], moveDependentContent: boolean): ContentItem[] {
    let contentItems: ContentItem[] = [];
    if (content.length > 0) {
      contentItems.push(...content.map(content => ({
        contentId: ((content.contentType === DatabaseContentType.name) || (content.contentType === TableContentType.name)) ? content.vizportalId :
                    (content.contentType === FlowContentType.name && content.draftStatus === ContentDraftStatus.unpublished) ? content.draftId :
                    content.id,
        contentType: (content.contentType === FlowContentType.name && content.draftStatus === ContentDraftStatus.unpublished) ? FlowDraftContentType.name : content.contentType,
        moveType: content.contentType === DatabaseContentType.name && moveDependentContent ? 'withChildContentItems' : null,
      })));
    }
    return contentItems;
  }

  const allContentTypes = [
    ProjectContentType,
    WorkbookContentType,
    DatasourceContentType,
    FlowContentType,
    DataRoleContentType,
    LensContentType,
    MetricContentType,
    PublishedConnectionContentType,
    PredictiveModelContentType,
    ... FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled ? [DatabaseContentType] : [],
    TableContentType
  ];

  function moveContentToContainer(containerId: string, containerType: string, contentItems: ContentItem[], contentItemInfos: IContentInfo[], projectName?: string): Promise<IMultipleContentTypeResult> {
    return Projects.moveToContainer(containerId, containerType, contentItems)
      .then((result: IMultipleContentTypeResult) => {
        const contentTypeToErrors = _.groupBy(result.errors, error => error.contentType);
        allContentTypes.forEach((contentType: IContentType) => {
          const contentTypeResult: IResult = {errors: contentTypeToErrors[contentType.name]};
          let contentTypeItems;
          let flowDraftContentTypeItems;
          if (contentType.name === DatabaseContentType.name) {
            contentTypeItems = new Set((item: IContentInfo) => item.vizportalId);
            contentTypeItems.addItems(contentItemInfos.filter(i => i.contentType === contentType.name));
          } else if (contentType.name === FlowContentType.name) {
            contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter((i, index) => (i.contentType === contentType.name && i.draftStatus !== ContentDraftStatus.unpublished)));
            flowDraftContentTypeItems = Set.fromIdItemArray(contentItemInfos.filter((i, index) => (i.contentType === contentType.name && i.draftStatus === ContentDraftStatus.unpublished)));
          } else {
            contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter(i => i.contentType === contentType.name));
          }
          if (contentTypeItems.size() > 0) {
            ContentActionNotification.displayItemizedToastsForResult(
              notificationForMoveContentToContainer(
              new MultipleItemResult(contentTypeItems, contentTypeResult),
              contentType.name as ExplorableItemType,
              containerType as DestinationItemType,
              projectName,
              containerId
            ));
          }
          if (flowDraftContentTypeItems?.size() > 0) {
            const project: IProjectName = {id: containerId, name: projectName};
            ContentActionNotification.displayItemizedToastsForResult(
              notification(
                new MultipleItemResult(flowDraftContentTypeItems, contentTypeResult),
                Messages.moveFlowDraftsToProjectSuccess,
                Messages.moveFlowDraftsToProjectError,
                FlowContentType,
                project
              )
            );
          }
        });
        return result;
      });
  }


  function moveProjects(projects: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (projects.size() < 1) {
      return Promise.resolve(null);
    }
    return Projects.moveToProject(destinationProjectId, projects.map(project => project.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(projects, result),
            Messages.moveProjectsToProjectSuccess,
            Messages.moveProjectsToProjectError,
            ProjectContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveDataRoles(dataRoles: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (dataRoles.size() < 1) {
      return Promise.resolve(null);
    }
    return DataRoles.moveToProject(destinationProjectId, dataRoles.map(dr => dr.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(dataRoles, result),
            Messages.moveDataRolesToProjectSuccess,
            Messages.moveDataRolesToProjectError,
            DataRoleContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function notification(
    result: MultipleItemResult<INamedItemInfo>,
    successId: (a: {
      ITEM_COUNT: number;
      ITEM_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    errorId: (a: {
      ERROR_COUNT: number;
      ERROR_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    itemType: IContentType,
    project: IProjectName): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          successId({
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ITEM_COUNT_LOC: Formatters.formatCount(successCount)
          }),

        describeErrorSummary: (errorCount: number) =>
          errorId({
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ERROR_COUNT_LOC: Formatters.formatCount(errorCount)
          }),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return ProjectErrors.errorDetailsFor(errorItem as IErrorItem<IProjectBase>);
          } else if (itemType === DatasourceContentType) {
            return DataSourceErrors.errorDetailsFor(errorItem as IErrorItem<IDataSourceBase>);
          } else if (itemType === DatabaseContentType) {
            return moveContentToContainerErrorMessageDetail(errorItem as IErrorItem<IDatabaseInfo>, 'database');
          } else if (itemType === WorkbookContentType) {
            return WorkbookErrors.errorDetailsFor(errorItem as IErrorItem<IWorkbookBase>);
          } else if (itemType === FlowContentType) {
            return FlowErrors.errorDetailsFor(errorItem as IErrorItem<FlowBaseInfo>);
          } else if (itemType === DataRoleContentType) {
            return DataRoleErrors.errorDetailsFor(errorItem as IErrorItem<IDataRoleBase>);
          } else if (itemType === LensContentType) {
            return LensErrors.errorDetailsFor(errorItem as IErrorItem<ILensBase>);
          } else if (itemType === MetricContentType) {
            return MetricErrors.errorDetailsFor(errorItem as IErrorItem<IMetricInfo>);
          } else if (itemType === PublishedConnectionContentType) {
            return PublishedConnectionErrors.errorDetailsFor(errorItem as IErrorItem<IPublishedConnectionInfo>);
          } else if (itemType === PredictiveModelContentType) {
            return PredictiveModelErrors.errorDetailsFor(errorItem as IErrorItem<IPredictiveModelInfo>);
          }
        }
      }
    );
  }

  function notificationForMoveContentToContainer(
    result: MultipleItemResult<INamedItemInfo>,
    itemTypeName: ExplorableItemType,
    destinationType: DestinationItemType,
    projectName?: string,
    projectId?: string): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          getSuccessMessageForMoveContentToContainer(destinationType, itemTypeName, successCount, projectName),

        successActionMessage: (successCount: number) => {
          switch (destinationType) {
            case DestinationItemType.personalSpace:
              return Messages.goToPersonalSpace();
            case DestinationItemType.project:
              return Messages.goToProject();
          }
        },

        describeErrorSummary: (errorCount: number) =>
          getErrorMessageForMoveContentToContainer(destinationType, itemTypeName, errorCount, projectName),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemTypeName === ProjectContentType.name) {
            return ProjectErrors.errorDetailsFor(errorItem as IErrorItem<IProjectBase>);
          } else if (itemTypeName === DatasourceContentType.name) {
            return DataSourceErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IDataSourceBase>, destinationType);
          } else if (itemTypeName === WorkbookContentType.name) {
            return WorkbookErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IWorkbookBase>, destinationType);
          } else if (itemTypeName === PredictiveModelContentType.name) {
            return PredictiveModelErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IPredictiveModelBase>, destinationType);
          } else if (itemTypeName === MetricContentType.name) {
            return MetricErrors.errorDetailsFor(errorItem as IErrorItem<IMetricInfo>, destinationType);
          } else if (itemTypeName === FlowContentType.name) {
            return FlowErrors.errorDetailsFor(errorItem as IErrorItem<FlowBaseInfo>, destinationType);
          } else if (itemTypeName === LensContentType.name) {
            return LensErrors.errorDetailsFor(errorItem as IErrorItem<ILensBase>);
          } else if (itemTypeName === PublishedConnectionContentType.name) {
            return PublishedConnectionErrors.errorDetailsFor(errorItem as IErrorItem<IPublishedConnectionInfo>);
          } else {
            return moveContentToContainerErrorMessageDetail(errorItem, itemTypeName);
          }
        }
      },
      successAction(destinationType, projectId),
    );
  }

  function moveContentToContainerErrorMessageDetail(errorItem: IErrorItem<INamedItemInfo>, itemTypeName: ExplorableItemType): IErrorDetail {
    return {
      itemName: errorItem.item.name,
      itemUrl: null,
      errorMessage: moveContentToContainerErrorMessageForContent(errorItem.error, itemTypeName)
    };
  }

  function moveContentToContainerErrorMessageForContent(error: IError, itemTypeName: ExplorableItemType): string {
    switch(error.code) {
      case ContentErrorCode.INVALID_CONTENT_TYPE:
      case CommonErrorCode.UnexpectedParameterValue: // projects
        return Messages.moveContentToPersonalSpaceErrorDetail({CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, true))});
      default:
        return Messages.errorUnknown();
    }
  }

  function successAction(containerType: DestinationItemType, projectId?: string) {
    return () => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      switch(containerType) {
        case DestinationItemType.personalSpace:
          Location.instance.setUrl(siteUrls.personalSpaceUrl());
          break;
        case DestinationItemType.project:
          Location.instance.setUrl(siteUrls.projectUrl(projectId));
          break;
        default:
          break;
      }
    };
  }

  function getSuccessMessageForMoveContentToContainer(containerType: DestinationItemType, itemType: ExplorableItemType, count: number, projectName?: string) {
    if (containerType === DestinationItemType.personalSpace) {
      return Messages.moveContentToPersonalSpaceSuccess({
        ITEM_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemType, count > 1))
      });
    } else {
      return Messages.moveContentToProjectSuccess({
        ITEM_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemType, count > 1)),
        PROJECT_NAME: projectName
      });
    }
  }

  function getErrorMessageForMoveContentToContainer(containerType: DestinationItemType, itemTypeName: ExplorableItemType, count: number, projectName?: string) {
    if (containerType === DestinationItemType.personalSpace) {
      return Messages.moveContentToPersonalSpaceError({
        ERROR_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1))
      });
    } else {
      return Messages.moveContentToProjectError({
        ERROR_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1)),
        PROJECT_NAME: projectName
      });
    }
  }

  function getLocalizedContentType(contentType: ExplorableItemType, isPlural: boolean): string {
    const typeDefinition = getExplorableItemDefinition(contentType);
    if (_.isUndefined(typeDefinition)) {
      throw new Error('Tried to localize unrecognized content type string: ' + contentType);
    }
    return isPlural ? typeDefinition.pluralName() : typeDefinition.singularName();
  }
}

export type MoveContentAction = typeof MoveContentAction;
